import {
  BACKGROUND_DIM_DEFAULT,
  BACKGROUND_DIM_MAX,
  BACKGROUND_DIM_MIN,
  BACKGROUND_STYLES,
  COLOR_MODES,
  DEFAULT_THEME,
  isTheme,
  toTheme,
  type Appearance,
  type BackgroundStyle,
  type ColorMode,
  type ThemeId,
} from '@recipes/contracts';

/**
 * How the app looks on this device (docs/partial/appearance-and-themes-spec.md 6.5).
 *
 * - The device remembers only what somebody chose (`rm.appearance`); what was never chosen
 *   follows the installation (`rm.appearance.defaultTheme`, from `/config`) and the defaults.
 * - `public/theme-boot.js` applies it before the first paint and is the one implementation of
 *   "apply": this module calls it again whenever the choice changes.
 * - Device and profile are brought together by the same rule as the language (see
 *   `reconcileAppearance`).
 */

export type AppearanceChoice = Partial<Appearance>;

const KEY = 'rm.appearance';
const DEFAULT_THEME_KEY = 'rm.appearance.defaultTheme';
/** sessionStorage: chosen by hand during this visit, before signing in. */
const EXPLICIT_KEY = 'rm.appearance.explicit';
/** localStorage: a choice that still has to reach the profile. */
const PENDING_KEY = 'rm.appearance.pending';

interface BootApi {
  apply(choice?: AppearanceChoice): {
    theme: string;
    colorMode: ColorMode;
    dark: boolean;
    backgroundStyle: BackgroundStyle;
  };
  onSystemChange(listener: () => void): () => void;
}
declare global {
  interface Window {
    rmAppearance?: BootApi;
  }
}

function read(storage: 'session' | 'local', key: string): string | null {
  try {
    return (storage === 'session' ? sessionStorage : localStorage).getItem(key);
  } catch {
    return null;
  }
}
function write(storage: 'session' | 'local', key: string, value: string | null): void {
  try {
    const target = storage === 'session' ? sessionStorage : localStorage;
    if (value === null) target.removeItem(key);
    else target.setItem(key, value);
  } catch {
    // storage unavailable: the choice still applies to this page
  }
}

/** Keeps only what is valid – a theme removed in a later version simply is no choice any more. */
export function sanitizeChoice(value: unknown): AppearanceChoice {
  if (!value || typeof value !== 'object') return {};
  const raw = value as Record<string, unknown>;
  const choice: AppearanceChoice = {};
  if (isTheme(raw.theme)) choice.theme = raw.theme;
  if ((COLOR_MODES as readonly unknown[]).includes(raw.colorMode))
    choice.colorMode = raw.colorMode as ColorMode;
  if ((BACKGROUND_STYLES as readonly unknown[]).includes(raw.backgroundStyle))
    choice.backgroundStyle = raw.backgroundStyle as BackgroundStyle;
  if (typeof raw.backgroundDim === 'number' && Number.isFinite(raw.backgroundDim))
    choice.backgroundDim = Math.min(
      BACKGROUND_DIM_MAX,
      Math.max(BACKGROUND_DIM_MIN, Math.round(raw.backgroundDim)),
    );
  return choice;
}

function readChoice(storage: 'session' | 'local', key: string): AppearanceChoice {
  try {
    return sanitizeChoice(JSON.parse(read(storage, key) ?? 'null'));
  } catch {
    return {};
  }
}
function writeChoice(storage: 'session' | 'local', key: string, choice: AppearanceChoice): void {
  write(storage, key, Object.keys(choice).length ? JSON.stringify(choice) : null);
}

export function installationTheme(): ThemeId {
  return toTheme(read('local', DEFAULT_THEME_KEY), DEFAULT_THEME);
}

/** `/config` told us the installation's default theme: visitors without a choice get it. */
export function rememberInstallationTheme(theme: string | null | undefined): void {
  if (!isTheme(theme) || theme === read('local', DEFAULT_THEME_KEY)) return;
  write('local', DEFAULT_THEME_KEY, theme);
  apply();
}

export function deviceChoice(): AppearanceChoice {
  return readChoice('local', KEY);
}

/** What is in effect on this device: the choice, completed with the defaults. */
export function currentAppearance(): Appearance {
  const choice = deviceChoice();
  return {
    theme: choice.theme ?? installationTheme(),
    colorMode: choice.colorMode ?? 'SYSTEM',
    backgroundStyle: choice.backgroundStyle ?? 'PATTERN',
    backgroundDim: choice.backgroundDim ?? BACKGROUND_DIM_DEFAULT,
  };
}

// --- a tiny store, so components follow a change made anywhere ------------------------------

const listeners = new Set<() => void>();
let snapshot: Appearance & { dark: boolean } = { ...currentAppearance(), dark: false };

let override: Appearance | null = null;

/**
 * A page that does not belong to the visitor's own app – a public share page – shows the
 * installation's default theme in the system's mode (spec 10, P9), whatever this device
 * remembers. Nothing is stored; `null` gives the device's look back.
 */
export function overrideAppearance(active: boolean): void {
  override = active
    ? {
        theme: installationTheme(),
        colorMode: 'SYSTEM',
        backgroundStyle: 'NONE',
        backgroundDim: BACKGROUND_DIM_DEFAULT,
      }
    : null;
  apply();
}

function apply(): void {
  const appearance = override ?? currentAppearance();
  const result = typeof window !== 'undefined' ? window.rmAppearance?.apply(appearance) : undefined;
  const dark =
    result?.dark ??
    (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  snapshot = { ...appearance, dark };
  listeners.forEach((listener) => listener());
}

let systemHooked = false;
export function subscribeAppearance(listener: () => void): () => void {
  if (!systemHooked && typeof window !== 'undefined' && window.rmAppearance) {
    systemHooked = true;
    window.rmAppearance.onSystemChange(apply);
  }
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export function appearanceSnapshot(): Appearance & { dark: boolean } {
  return snapshot;
}

/** Applies what the device remembers; called once when the app starts. */
export function initAppearance(): void {
  apply();
}

function setDeviceChoice(choice: AppearanceChoice): void {
  writeChoice('local', KEY, choice);
  apply();
}

// --- device and profile -------------------------------------------------------------------

export function pendingAppearance(): AppearanceChoice {
  return readChoice('local', PENDING_KEY);
}

/**
 * Somebody chose something. It applies at once and stays on the device; `signedIn` decides
 * whether the profile has to follow now or at the next sign-in.
 */
export function chooseAppearance(patch: AppearanceChoice, signedIn: boolean): void {
  const clean = sanitizeChoice(patch);
  setDeviceChoice({ ...deviceChoice(), ...clean });
  const [storage, key] = signedIn
    ? (['local', PENDING_KEY] as const)
    : (['session', EXPLICIT_KEY] as const);
  writeChoice(storage, key, { ...readChoice(storage, key), ...clean });
}

export type SaveAppearance = (patch: AppearanceChoice) => Promise<Appearance>;

const differs = (patch: AppearanceChoice, from: Appearance): AppearanceChoice =>
  Object.fromEntries(
    Object.entries(patch).filter(([key, value]) => from[key as keyof Appearance] !== value),
  ) as AppearanceChoice;

/**
 * Brings device and profile together – the rule of the language (localization spec 5.2):
 * signing in applies the profile to the device, unless something was chosen by hand during this
 * visit: then that is written to the profile. A choice that could not be saved (offline) waits
 * and is saved once. Returns what the profile holds afterwards, or null when nothing was saved.
 */
export async function reconcileAppearance(
  profile: Appearance,
  save: SaveAppearance,
  online: boolean,
): Promise<Appearance | null> {
  const explicit = readChoice('session', EXPLICIT_KEY);
  if (Object.keys(explicit).length) {
    // Chosen on the login page a moment ago: that is what the user wants from now on.
    writeChoice('session', EXPLICIT_KEY, {});
    writeChoice('local', PENDING_KEY, { ...pendingAppearance(), ...explicit });
  }
  const pending = differs(pendingAppearance(), profile);
  if (!Object.keys(pending).length) {
    writeChoice('local', PENDING_KEY, {});
    // The profile wins – written as a whole, so a second device looks like the first.
    if (JSON.stringify(currentAppearance()) !== JSON.stringify(profile)) setDeviceChoice(profile);
    return null;
  }
  // What is pending stays in effect on this device; the rest follows the profile.
  setDeviceChoice({ ...profile, ...pending });
  if (!online) return null;
  try {
    const saved = await save(pending);
    // Only forget what was saved – another choice may have been made meanwhile.
    const still = pendingAppearance();
    writeChoice(
      'local',
      PENDING_KEY,
      Object.fromEntries(
        Object.entries(still).filter(([key, value]) => pending[key as keyof Appearance] !== value),
      ) as AppearanceChoice,
    );
    return saved;
  } catch {
    return null; // stays pending; the next sign-in, profile load or reconnect tries again
  }
}

/** Signing out keeps the device's look but forgets what was meant for the old profile. */
export function forgetPendingAppearance(): void {
  writeChoice('local', PENDING_KEY, {});
}

/** The next mode of the quick switch: Light → Dark → System → Light. */
export function nextColorMode(mode: ColorMode): ColorMode {
  return mode === 'LIGHT' ? 'DARK' : mode === 'DARK' ? 'SYSTEM' : 'LIGHT';
}
